// ---------------------------------
// client/src/pages/JobDetails.jsx
// Detailed view for a single job and its associated PDF forms
// ---------------------------------
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { useNavigate, useParams } from 'react-router-dom';
import {
    fetchJobDetail,
    fetchFormTemplates,
    addJobForm,
    duplicateJobForm,
    deleteJob,
    updateJobDetails,
    updateJobStatus,
    removeJobForm,
    fetchJobGallery,
    downloadJobMergedPdf,
    downloadJobGalleryZip,
    uploadJobGalleryPhoto,
    deleteJobGalleryPhoto,
} from '../utils/jobs';
import { fetchAvailablePdfTemplates } from '../utils/pdfTemplates';
import {
    fetchPdfForm,
    sharePdfFormByEmail,
    storeCachedPdfFormExport,
    downloadCachedPdfFormExport,
} from '../utils/pdfForms';
import { fetchCurrentUser } from '../utils/users';
import { fetchRecipients } from '../utils/recipients';
import { queueFinalizedJobEmail, cancelQueuedEmail, fetchEmailStatus } from '../utils/emails';
import { saveBlob } from '../utils/download';
import { exportPdfFormWithOverlays, buildFormFilename } from '../utils/pdfExport';
import { formatDate } from '../utils/date';
import { resolveAssetUrl } from '../utils/assets';
import PdfThumbnail from '../components/PdfThumbnail';

const EMAIL_RECIPIENT_LIMIT = 50;

const TEXT = {
    title: 'פרטי עבודה',
    back: 'חזור',
    forms: 'טפסי עבודה',
    noFormsAttached: 'אין טפסים משויכים לעבודה זו.',
    status: 'סטטוס',
    updatedAt: 'עודכן לאחרונה',
    jobTypeLabel: 'סוג עבודה',
    recipientLabel: 'שם החברה המקבלת שירות',
    openedAtLabel: 'נפתח בתאריך',
    openedByLabel: 'נפתח על ידי',
    loading: 'טוען...',
    loadError: 'לא ניתן לטעון את פרטי העבודה.',
    openForm: 'פתח טופס',
    downloadForm: 'הורד PDF',
    downloadingForm: 'מוריד...',
    progressTitle: 'התקדמות טפסים',
    progressCaption: 'טפסים הושלמו',
    progressNone: 'לא צורפו טפסים עדיין.',
    downloadMerged: 'סמן כהושלם ושלח בדוא"ל',
    downloadingMerged: 'מוריד קובץ...',
    markFinishedConfirm: 'לסמן את העבודה כהושלמה ולשלוח את הטפסים בדוא"ל?',
    markFinishedMissing: 'לא ניתן לסמן עבודה כהושלמה כשיש טפסי חובה שלא הושלמו.',
    markFinishedError: 'לא ניתן לסמן עבודה כהושלמה.',
    reopenJob: 'פתח מחדש לעריכה',
    reopeningJob: 'פותח מחדש...',
    reopenConfirm: 'לפתוח מחדש את העבודה לעריכה?',
    jobReopenSuccess: 'העבודה נפתחה מחדש לעריכה.',
    jobReopenError: 'לא ניתן לפתוח מחדש את העבודה.',
    downloadError: 'לא ניתן להוריד את הקובץ.',
    addForm: 'הוסף טופס',
    addFormTitle: 'הוספת טופס לעבודה',
    addFormDescription: 'בחר טופס PDF קיים לצירוף ידני לעבודה.',
    addFormSearch: 'חיפוש טפסים',
    addFormPlaceholder: 'הקלד שם טופס',
    addFormSubmit: 'צרף',
    addFormCancel: 'ביטול',
    addFormSuccess: 'הטופס צורף לעבודה.',
    addFormError: 'לא ניתן לצרף את הטופס.',
    addEmptyRepeatable: 'הוסף ריק',
    duplicateLatestForm: 'שכפל אחרון',
    duplicatingForm: 'משכפל...',
    duplicateForm: 'שכפל טופס',
    duplicateFormMenu: 'אפשרויות שכפול טופס',
    previousFormInstance: 'לטופס הקודם',
    nextFormInstance: 'לטופס הבא',
    repeatableForm: 'טופס חוזר',
    formInstance: 'טופס',
    formInstances: 'טפסים',
    addFormSelectionRequired: 'יש לבחור טופס לצירוף.',
    addFormLoadError: 'לא ניתן לטעון את רשימת הטפסים.',
    addFormClosed: 'לא ניתן לצרף טופס לעבודה שסומנה כהושלמה.',
    noMatchingForms: 'לא נמצאו טפסים תואמים.',
    allFormsAttached: 'כל הטפסים כבר משויכים לעבודה זו.',
    requiredLabel: 'חובה',
    manualLabel: 'ידני',
    notesTitle: 'הערות עבודה',
    notesEmpty: 'אין הערות עבור עבודה זו.',
    notesEdit: 'ערוך הערות',
    notesSave: 'שמור',
    notesSaving: 'שומר...',
    notesCancel: 'ביטול',
    notesSaved: 'ההערות נשמרו.',
    notesError: 'לא ניתן לשמור את ההערות.',
    removeForm: 'הסר טופס',
    removingForm: 'מסיר...',
    removeFormSuccess: 'הטופס הוסר מהעבודה.',
    removeFormError: 'לא ניתן להסיר את הטופס.',
    removeFormConfirm: 'להסיר את הטופס מהעבודה?',
    removeFormClosed: 'לא ניתן להסיר טופס מעבודה שהושלמה.',
    showThumbnails: 'הצג תמונות ממוזערות',
    archiveJob: 'העבר לארכיון',
    restoreJob: 'החזר מארכיון',
    archivingJob: 'מעביר לארכיון...',
    restoringJob: 'משחזר...',
    jobArchiveSuccess: 'העבודה הועברה לארכיון.',
    jobRestoreSuccess: 'העבודה הוחזרה מארכיון.',
    jobArchiveError: 'לא ניתן לעדכן את סטטוס העבודה.',
    archiveConfirm: 'להעביר את העבודה לארכיון?',
    restoreConfirm: 'להחזיר את העבודה מארכיון?',
    sendEmail: 'שליחה בדוא"ל',
    sendEmailTitle: 'שליחת טפסים בדוא"ל',
    sendEmailDescription: 'בחר נמען אחד או יותר לשליחת הטפסים המאוחדים.',
    sendEmailTo: 'בחר נמענים',
    sendEmailSelected: 'נמענים שנבחרו',
    sendEmailRemoveRecipient: 'הסר נמען',
    sendEmailSubmit: 'שלח וסיים',
    sendEmailCancel: 'ביטול',
    sendEmailQueued: 'ההודעה תישלח בעוד',
    sendEmailUndo: 'בטל שליחה',
    sendEmailCancelled: 'השליחה בוטלה',
    sendEmailSent: 'ההודעה נשלחה',
    sendEmailFailed: 'לא ניתן לשלוח את ההודעה',
    sendEmailNoForms: 'לא ניתן לשלוח ללא טפסים מושלמים.',
    sendEmailInvalid: 'כתובת אימייל לא תקינה.',
    sendEmailNoRecipients: 'לא הוגדרו נמענים.',
    shareForm: 'שתף',
    shareAllForms: 'שתף הכל',
    shareFormSent: 'נשלח',
    shareFormTitle: 'שיתוף טופס בדוא"ל',
    shareFormDescription: 'בחר נמענים מהרשימה או הוסף כתובות דוא"ל נוספות.',
    shareFormTo: 'הוסף כתובת דוא"ל',
    shareFormAddRecipient: 'הוסף נמען',
    shareFormSelectedRecipients: 'נמענים שנבחרו',
    shareFormSubmit: 'שלח',
    shareFormSending: 'שולח...',
    shareFormSuccess: 'הטופס נשלח.',
    shareFormError: 'לא ניתן לשלוח את הטופס.',
    shareFormCompletedOnly: 'ניתן לשתף רק טופס שהושלם.',
    shareFormAlreadySent: 'הטופס כבר נשלח.',
    shareFormNotEnabled: 'שיתוף לא מופעל לתבנית זו.',
    shareFormInvalid: 'כתובת דוא"ל לא תקינה.',
    shareFormNoRecipients: 'יש לבחור או להוסיף לפחות נמען אחד.',
    shareFormDuplicate: 'כתובת זו כבר נבחרה.',
    downloadMergedOnly: 'הורד PDF מאוחד',
    sendEmailSelectRecipient: 'בחר לפחות נמען אחד מהרשימה.',
    sendEmailTooManyRecipients: `ניתן לשלוח עד ${EMAIL_RECIPIENT_LIMIT} נמענים במייל אחד.`,
    emailQueuedLock: 'לא ניתן לפתוח מחדש לעריכה בזמן שהודעה מתוזמנת לשליחה. יש לבטל את השליחה תחילה.',
    galleryTitle: 'גלריית תמונות',
    galleryDescription: 'הוספת תמונות מהשטח שישלחו כקישור הורדה במייל הסגירה.',
    galleryUpload: 'העלה תמונה',
    galleryCamera: 'צלם תמונה',
    galleryDownloadAll: 'הורד את כל התמונות',
    galleryDownloading: 'מוריד...',
    galleryDelete: 'מחק תמונה',
    galleryDeleteConfirm: 'למחוק את התמונה מהגלריה?',
    galleryLoading: 'טוען תמונות...',
    galleryEmpty: 'עדיין לא נוספו תמונות לעבודה זו.',
    galleryUploadError: 'לא ניתן להעלות את התמונה.',
    galleryDownloadError: 'לא ניתן להוריד את התמונות.',
    galleryDeleteError: 'לא ניתן למחוק את התמונה.',
};


TEXT.deleteJobPermanent = 'מחיקה מלאה';
TEXT.deleteJobConfirm = 'למחוק לגמרי את העבודה ואת כל הטפסים והתמונות המשויכים אליה?';
TEXT.deleteJobError = 'לא ניתן למחוק את העבודה.';
TEXT.showMetadata = 'הצג פרטים נוספים';
TEXT.hideMetadata = 'הסתר פרטים נוספים';

const STATUS_CLASS = {
    open: 'status-badge--warning',
    pending: 'status-badge--warning',
    in_progress: 'status-badge--info',
    completed: 'status-badge--success',
    archived: 'status-badge--danger',
};

const STATUS_LABEL = {
    open: 'פתוח',
    pending: 'ממתין',
    in_progress: 'בתהליך',
    completed: 'הושלם',
    archived: 'בארכיון',
};


const FORM_STATUS_LABEL = {
    pending: 'ממתין',
    in_progress: 'בתהליך',
    completed: 'הושלם',
};



const FORM_STATUS_ORDER = {
    in_progress: 0,
    pending: 1,
    completed: 2,
};
const EDIT_LOCK_TTL_MS = 3 * 60 * 1000;
const EMAIL_STATUS_BANNER_MS = 15000;
const PANEL_ICON_PROPS = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
};

function normalizeFormStatus(status) {
    const raw = (status || '').toLowerCase();
    if (raw === 'submitted') return 'completed';
    if (raw === 'draft') return 'in_progress';
    return 'pending';
}

function summarizeText(value, maxLength = 110) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength).trim()}...`;
}

function isValidEmailAddress(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function uniqueEmails(values) {
    const seen = new Set();
    const emails = [];
    values.forEach((value) => {
        const email = String(value || '').trim();
        if (!email) return;
        const key = email.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        emails.push(email);
    });
    return emails;
}

function formatFileSize(sizeBytes) {
    const size = Number(sizeBytes);
    if (!Number.isFinite(size) || size <= 0) return '';
    if (size < 1024) return `${Math.round(size)} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function getFormInstanceNumber(form) {
    const value = Number(form?.instanceNumber);
    return Number.isFinite(value) && value > 0 ? value : 1;
}

function getFormDownloadName(form) {
    const name = form?.name || form?.template?.name || 'form';
    const instanceNumber = getFormInstanceNumber(form);
    return instanceNumber > 1 ? `${name} ${instanceNumber}` : name;
}

function getPositiveOrder(value) {
    const order = Number(value);
    return Number.isFinite(order) && order > 0 ? order : Number.MAX_SAFE_INTEGER;
}

function compareFormsForJobOrder(a, b) {
    const displayRank = getPositiveOrder(a.displayOrder) - getPositiveOrder(b.displayOrder);
    if (displayRank !== 0) return displayRank;
    const jobTypeRank =
        (a.jobTypeOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.jobTypeOrder ?? Number.MAX_SAFE_INTEGER);
    if (jobTypeRank !== 0) return jobTypeRank;
    const nameRank = (a.name || '').localeCompare(b.name || '', 'he');
    if (nameRank !== 0) return nameRank;
    const numberRank = getFormInstanceNumber(a) - getFormInstanceNumber(b);
    if (numberRank !== 0) return numberRank;
    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return createdA - createdB;
}

function formatDateTime(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    const dateLabel = formatDate(value);
    if (Number.isNaN(date.getTime())) {
        return dateLabel;
    }
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${dateLabel} ${hours}:${minutes}`;
}

function PanelChevronIcon({ expanded }) {
    return (
        <svg {...PANEL_ICON_PROPS} aria-hidden="true">
            <path
                d={expanded ? 'M6.5 14.5 12 9l5.5 5.5' : 'M6.5 9.5 12 15l5.5-5.5'}
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function NotesIcon() {
    return (
        <svg {...PANEL_ICON_PROPS} aria-hidden="true">
            <rect x="5" y="4.5" width="14" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 9h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 12.5h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}

function GalleryIcon() {
    return (
        <svg {...PANEL_ICON_PROPS} aria-hidden="true">
            <rect x="4.5" y="5.5" width="15" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="9" cy="10" r="1.6" fill="currentColor" />
            <path
                d="m7 16 3.2-3.4a1 1 0 0 1 1.47 0L14 15l2.15-2.18a1 1 0 0 1 1.42 0L19 14.3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SharePdfIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M7 4.5h6l4 4v11a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 6 19.5V6A1.5 1.5 0 0 1 7.5 4.5Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
            />
            <path d="M13 4.5v4h4" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M10 14h5m0 0-2-2m2 2-2 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function SharedCheckIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.7" />
            <path d="m8.5 12.2 2.2 2.2 4.8-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function DownloadIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 4v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

function PlusIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
    );
}

function CameraIcon({ size = 22 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 9a2 2 0 0 1 2-2h2.1l1.3-2h5.2l1.3 2H18a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
    );
}

function ImageUploadIcon({ size = 22 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4.5" y="5" width="15" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
            <circle cx="9" cy="10" r="1.5" fill="currentColor" />
            <path d="m6.8 16 3.2-3.1 2.4 2.3 1.5-1.6 3.3 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15.5 4.5v5M13 7h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

function DuplicateIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="8" y="7" width="10" height="12" rx="2" stroke="currentColor" strokeWidth="1.7" />
            <path d="M6 15H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <path d="M13 10.5v5M10.5 13h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

function ArrowIcon({ direction = 'next', size = 18 }) {
    const path = direction === 'previous' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7';
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d={path} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ThumbnailIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="4.5" width="14" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
            <path d="M8 8h8M8 11.5h8M8 15h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

export default function JobDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const thumbnailPrefKey = 'jobDetails_showThumbnails';
    const [job, setJob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [notesError, setNotesError] = useState('');
    const [notesFeedback, setNotesFeedback] = useState('');
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [availableForms, setAvailableForms] = useState([]);
    const [formSearch, setFormSearch] = useState('');
    const [selectedFormId, setSelectedFormId] = useState('');
    const [formActionError, setFormActionError] = useState('');
    const [isAddingForm, setIsAddingForm] = useState(false);
    const [addingRepeatableTemplates, setAddingRepeatableTemplates] = useState(() => new Set());
    const [duplicatingForms, setDuplicatingForms] = useState(() => new Set());
    const [duplicateMenuKey, setDuplicateMenuKey] = useState('');
    const [repeatableFormIndices, setRepeatableFormIndices] = useState({});
    const [removingForms, setRemovingForms] = useState(() => new Set());
    const [isArchivingJob, setIsArchivingJob] = useState(false);
    const [downloadingForms, setDownloadingForms] = useState(() => new Set());
    const [isDownloadingMerged, setIsDownloadingMerged] = useState(false);
    const [isReopeningJob, setIsReopeningJob] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [finishError, setFinishError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [recipientsError, setRecipientsError] = useState('');
    const [selectedRecipientIds, setSelectedRecipientIds] = useState(() => new Set());
    const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);
    const [sendEmailError, setSendEmailError] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailJob, setEmailJob] = useState(null);
    const [emailCountdown, setEmailCountdown] = useState(0);
    const [isCancellingEmail, setIsCancellingEmail] = useState(false);
    const [shareFormTarget, setShareFormTarget] = useState(null);
    const [shareFormEmail, setShareFormEmail] = useState('');
    const [shareFormSelectedRecipientIds, setShareFormSelectedRecipientIds] = useState(() => new Set());
    const [shareFormExtraEmails, setShareFormExtraEmails] = useState([]);
    const [shareFormError, setShareFormError] = useState('');
    const [isSharingForm, setIsSharingForm] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState([]);
    const [isGalleryLoading, setIsGalleryLoading] = useState(false);
    const [galleryError, setGalleryError] = useState('');
    const [isGalleryUploading, setIsGalleryUploading] = useState(false);
    const [isGalleryDownloading, setIsGalleryDownloading] = useState(false);
    const [deletingGalleryPhotos, setDeletingGalleryPhotos] = useState(() => new Set());
    const [activeGalleryPhoto, setActiveGalleryPhoto] = useState(null);
    const [isNotesExpanded, setIsNotesExpanded] = useState(false);
    const [isGalleryExpanded, setIsGalleryExpanded] = useState(false);
    const [isJobMetaVisible, setIsJobMetaVisible] = useState(false);
    const [showThumbnails, setShowThumbnails] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.localStorage.getItem(thumbnailPrefKey) === 'true';
    });

    const isAdminUser = useMemo(() => {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
            return false;
        }
        try {
            const payload = jwtDecode(storedToken);
            return payload?.role === 'admin';
        } catch (err) {
            return false;
        }
    }, []);
    const isJobReadOnly = job?.status === 'completed' || job?.status === 'archived';
    const isEmailQueued = emailJob?.status === 'queued';
    const isGalleryLocked = isJobReadOnly || isEmailQueued;
    const emailJobStorageKey = useMemo(() => {
        if (!job?.id) return null;
        return `emailJob:${job.id}`;
    }, [job?.id]);
    const clearStoredEmailJob = useCallback(() => {
        if (!emailJobStorageKey || typeof window === 'undefined') return;
        window.localStorage.removeItem(emailJobStorageKey);
    }, [emailJobStorageKey]);

    const jobTypeOrderMap = useMemo(() => {
        const map = new Map();
        (job?.jobTypes || []).forEach((jobType, index) => {
            const order = Number.isFinite(Number(jobType.displayOrder))
                ? Number(jobType.displayOrder)
                : Number.MAX_SAFE_INTEGER + index;
            map.set(String(jobType.id), order);
        });
        return map;
    }, [job]);

    const loadJob = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');
            const jobResponse = await fetchJobDetail(id);
            setJob(jobResponse);
        } catch (err) {
            setError(err.response?.data?.message || TEXT.loadError);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadJob();
    }, [loadJob]);

    const loadGallery = useCallback(async () => {
        if (!id) return;
        try {
            setIsGalleryLoading(true);
            setGalleryError('');
            const data = await fetchJobGallery(id);
            setGalleryPhotos(Array.isArray(data) ? data : []);
        } catch (err) {
            setGalleryError(err.response?.data?.message || TEXT.galleryUploadError);
        } finally {
            setIsGalleryLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadGallery();
    }, [loadGallery]);

    useEffect(() => {
        let mounted = true;
        fetchCurrentUser()
            .then((data) => {
                if (mounted) setCurrentUser(data);
            })
            .catch(() => {
                if (mounted) setCurrentUser(null);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        let mounted = true;
        fetchRecipients()
            .then((data) => {
                if (mounted) {
                    setRecipients(Array.isArray(data) ? data : []);
                    setRecipientsError('');
                }
            })
            .catch(() => {
                if (mounted) {
                    setRecipients([]);
                    setRecipientsError(TEXT.sendEmailFailed);
                }
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!emailJobStorageKey || typeof window === 'undefined') return;
        const storedId = window.localStorage.getItem(emailJobStorageKey);
        if (!storedId) return;
        fetchEmailStatus(storedId)
            .then((status) => {
                if (!status?.id) {
                    clearStoredEmailJob();
                    return;
                }
                if (status.status !== 'queued') {
                    clearStoredEmailJob();
                    return;
                }
                setEmailJob((prev) => {
                    if (prev?.id && prev.id === status?.id) {
                        return { ...(prev || {}), ...status };
                    }
                    return status;
                });
            })
            .catch(() => {
                clearStoredEmailJob();
            });
    }, [clearStoredEmailJob, emailJobStorageKey]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(thumbnailPrefKey, showThumbnails ? 'true' : 'false');
    }, [showThumbnails]);

    useEffect(() => {
        const validIds = new Set(recipients.map((recipient) => String(recipient.id)));
        setSelectedRecipientIds((prev) => {
            const next = new Set(Array.from(prev).filter((id) => validIds.has(String(id))));
            if (next.size === 0 && recipients.length > 0) {
                const defaultRecipient =
                    recipients.find((recipient) => recipient.isDefaultForFinishedJobs) || recipients[0];
                next.add(String(defaultRecipient.id));
            }
            return next;
        });
        setShareFormSelectedRecipientIds((prev) => (
            new Set(Array.from(prev).filter((id) => validIds.has(String(id))))
        ));
    }, [recipients]);

    useEffect(() => {
        setFeedback('');
        setFormActionError('');
        setSelectedFormId('');
        setDownloadError('');
        setFinishError('');
        setSendEmailError('');
        setEmailJob(null);
        setEmailCountdown(0);
        setSelectedRecipientIds(new Set());
        setShareFormTarget(null);
        setShareFormEmail('');
        setShareFormSelectedRecipientIds(new Set());
        setShareFormExtraEmails([]);
        setShareFormError('');
        setActiveGalleryPhoto(null);
        setIsNotesExpanded(false);
        setIsGalleryExpanded(false);
        setDuplicateMenuKey('');
    }, [id]);

    useEffect(() => {
        if (!job || isEditingNotes) {
            return;
        }
        setNotesDraft(job.notes || '');
        setNotesError('');
        setNotesFeedback('');
    }, [job?.notes, isEditingNotes]);

    const enhancedForms = useMemo(() => {
        const forms = job?.pdfForms || [];
        const now = Date.now();
        const mapped = forms.map((form) => {
            const status = normalizeFormStatus(form.status);
            const editingAt = form.editingAt ? new Date(form.editingAt).getTime() : 0;
            const isEditing = Boolean(
                form.editingUserId &&
                    editingAt &&
                    Number.isFinite(editingAt) &&
                    now - editingAt < EDIT_LOCK_TTL_MS,
            );
            const editingUserName = form.editingUser?.name || form.editingUser?.email || '';
            const isRequired = Boolean(form.jobTypeTemplate?.isRequired);
            const isRequiredActive = isRequired && status !== 'completed';
            const jobTypeId = form.jobTypeTemplate?.jobTypeId;
            const jobTypeOrder = jobTypeId
                ? jobTypeOrderMap.get(String(jobTypeId)) ?? Number.MAX_SAFE_INTEGER
                : Number.MAX_SAFE_INTEGER;
            const parsedDisplayOrder = Number(form.jobTypeTemplate?.displayOrder);
            const displayOrder = Number.isFinite(parsedDisplayOrder) && parsedDisplayOrder > 0
                ? parsedDisplayOrder
                : Number.MAX_SAFE_INTEGER;

            return {
                ...form,
                name: form.template?.name || 'שם טופס לא ידוע',
                status,
                statusLabel: FORM_STATUS_LABEL[status] || status,
                statusClass: STATUS_CLASS[status] || 'status-badge--warning',
                jobFormId: form.id,
                updatedAt: form.updatedAt,
                instanceNumber: getFormInstanceNumber(form),
                isRequired,
                isRequiredActive,
                addedManually: !form.jobTypeTemplateId,
                displayOrder,
                jobTypeOrder,
                isEditing: isEditing && Boolean(editingUserName),
                editingLabel: isEditing && editingUserName ? editingUserName : '',
                isShareableByEmail: Boolean(form.template?.isShareableByEmail),
                isRepeatableInJobs: Boolean(form.template?.isRepeatableInJobs),
                isSharedByEmail: Boolean(form.shareEmailSentAt),
                hasCachedPdf: Boolean(form.cachedPdfGeneratedAt || form.cachedPdfSizeBytes),
            };
        });
        return mapped.sort((a, b) => {
            const orderRank = compareFormsForJobOrder(a, b);
            if (orderRank !== 0) {
                return orderRank;
            }
            const requiredRank = Number(!a.isRequiredActive) - Number(!b.isRequiredActive);
            if (requiredRank !== 0) {
                return requiredRank;
            }
            const statusRank =
                (FORM_STATUS_ORDER[a.status] ?? 99) - (FORM_STATUS_ORDER[b.status] ?? 99);
            if (statusRank !== 0) {
                return statusRank;
            }
            const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return dateB - dateA;
        });
    }, [job, jobTypeOrderMap]);

    const formGroups = useMemo(() => {
        const groups = [];
        const byTemplate = new Map();

        enhancedForms.forEach((form, index) => {
            const key = String(form.templateId || form.template?.id || form.name);
            if (!byTemplate.has(key)) {
                byTemplate.set(key, {
                    id: key,
                    forms: [],
                    firstIndex: index,
                });
            }
            const group = byTemplate.get(key);
            group.forms.push(form);
            group.firstIndex = Math.min(group.firstIndex, index);
        });

        byTemplate.forEach((group) => {
            const sortedForms = group.forms.slice().sort((a, b) => {
                const numberRank = getFormInstanceNumber(a) - getFormInstanceNumber(b);
                if (numberRank !== 0) return numberRank;
                const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return createdA - createdB;
            });
            if (sortedForms.length === 1) {
                groups.push({
                    id: sortedForms[0].jobFormId,
                    isRepeatable: false,
                    forms: sortedForms,
                    primaryForm: sortedForms[0],
                    firstIndex: group.firstIndex,
                });
                return;
            }
            const completedForms = sortedForms.filter((form) => form.status === 'completed');
            const latestForm = sortedForms[sortedForms.length - 1] || null;
            const shareTarget = completedForms.find((form) => !form.isSharedByEmail) || completedForms[0] || latestForm;
            const status =
                sortedForms.some((form) => form.status !== 'completed')
                    ? 'in_progress'
                    : 'completed';
            groups.push({
                ...group,
                isRepeatable: true,
                forms: sortedForms,
                primaryForm: latestForm,
                latestForm,
                shareTarget,
                completedForms,
                status,
                statusLabel: `${completedForms.length}/${sortedForms.length} ${TEXT.progressCaption}`,
                statusClass: STATUS_CLASS[status] || 'status-badge--warning',
                isRequiredActive: sortedForms.some((form) => form.isRequiredActive),
                isShareableByEmail: sortedForms.some((form) => form.isShareableByEmail),
                isSharedByEmail:
                    completedForms.length > 0 && completedForms.every((form) => form.isSharedByEmail),
            });
        });

        return groups.sort((a, b) => a.firstIndex - b.firstIndex);
    }, [enhancedForms]);

    const jobTypeNames = useMemo(() => {
        if (!job) {
            return [];
        }
        const names = new Set();
        if (typeof job.jobType?.name === 'string' && job.jobType.name.trim()) {
            names.add(job.jobType.name.trim());
        }
        (job.jobTypes || []).forEach((jobType) => {
            if (typeof jobType?.name === 'string' && jobType.name.trim()) {
                names.add(jobType.name.trim());
            }
        });
        return Array.from(names);
    }, [job]);

    const selectedRecipients = useMemo(
        () => recipients.filter((recipient) => selectedRecipientIds.has(String(recipient.id))),
        [recipients, selectedRecipientIds],
    );
    const selectedRecipientEmails = useMemo(
        () => uniqueEmails(selectedRecipients.map((recipient) => recipient.email)),
        [selectedRecipients],
    );
    const shareFormSelectedRecipients = useMemo(
        () => recipients.filter((recipient) => shareFormSelectedRecipientIds.has(String(recipient.id))),
        [recipients, shareFormSelectedRecipientIds],
    );
    const shareFormRecipientEmails = useMemo(
        () => uniqueEmails([
            ...shareFormSelectedRecipients.map((recipient) => recipient.email),
            ...shareFormExtraEmails,
        ]),
        [shareFormSelectedRecipients, shareFormExtraEmails],
    );

    const formProgress = useMemo(() => {
        const total = enhancedForms.length;
        const completed = enhancedForms.filter((form) => form.status === 'completed').length;
        return {
            total,
            completed,
            percent: total > 0 ? Math.round((completed / total) * 100) : 0,
        };
    }, [enhancedForms]);
    const notesSummary = useMemo(
        () => summarizeText(job?.notes) || TEXT.notesEmpty,
        [job?.notes],
    );
    const gallerySummary = useMemo(() => {
        if (galleryPhotos.length === 0) {
            return TEXT.galleryEmpty;
        }
        return `${galleryPhotos.length} ${TEXT.galleryTitle}`;
    }, [galleryPhotos]);

    const requiredIncomplete = useMemo(
        () => enhancedForms.filter((form) => form.isRequired && form.status !== 'completed'),
        [enhancedForms],
    );

    const startEditingNotes = () => {
        setIsNotesExpanded(true);
        setIsEditingNotes(true);
        setNotesError('');
        setNotesFeedback('');
    };

    const handleToggleNotesPanel = () => {
        setIsNotesExpanded((prev) => !prev);
    };

    const handleToggleGalleryPanel = () => {
        setIsGalleryExpanded((prev) => !prev);
    };

    const cancelEditingNotes = () => {
        setIsEditingNotes(false);
        setNotesError('');
        setNotesFeedback('');
        setNotesDraft(job?.notes || '');
    };

    const handleSaveNotes = async () => {
        if (!job) return;
        setNotesSaving(true);
        setNotesError('');
        setNotesFeedback('');
        try {
            const payload = { notes: notesDraft.trim() };
            const updated = await updateJobDetails(job.id, payload);
            setJob((prev) => ({
                ...(prev || {}),
                notes: updated?.notes ?? payload.notes,
                updatedAt: updated?.updatedAt ?? prev?.updatedAt,
            }));
            setNotesFeedback(TEXT.notesSaved);
            setIsEditingNotes(false);
        } catch (err) {
            setNotesError(err.response?.data?.message || TEXT.notesError);
        } finally {
            setNotesSaving(false);
        }
    };

    const loadAvailableForms = useCallback(async () => {
        try {
            setFormActionError('');
            const templates = isAdminUser
                ? await fetchFormTemplates()
                : await fetchAvailablePdfTemplates();
            const attachedIds = new Set((job?.pdfForms || []).map((form) => form.templateId));
            const filtered = templates.filter((template) => !attachedIds.has(template.id));
            setAvailableForms(filtered);
        } catch (err) {
            setAvailableForms([]);
            setFormActionError(TEXT.addFormLoadError);
        }
    }, [isAdminUser, job]);

    const filteredAvailableForms = useMemo(() => {
        const term = formSearch.trim().toLocaleLowerCase('he-IL');
        if (!term) {
            return availableForms;
        }
        return availableForms.filter((form) => (form.name || '').toLocaleLowerCase('he-IL').includes(term));
    }, [availableForms, formSearch]);

    const openAddFormModal = useCallback(async () => {
        if (isLoading) return;
        if (isJobReadOnly) {
            setFormActionError(TEXT.addFormClosed);
            return;
        }
        setFormActionError('');
        setSelectedFormId('');
        setFormSearch('');
        await loadAvailableForms();
        setIsAddFormOpen(true);
    }, [isJobReadOnly, isLoading, loadAvailableForms]);

    const closeAddFormModal = () => {
        if (isAddingForm) return;
        setIsAddFormOpen(false);
        setSelectedFormId('');
        setFormActionError('');
    };

    const handleAddForm = async (event) => {
        event.preventDefault();
        if (isJobReadOnly) {
            setFormActionError(TEXT.addFormClosed);
            return;
        }
        if (!selectedFormId) {
            setFormActionError(TEXT.addFormSelectionRequired);
            return;
        }
        try {
            setIsAddingForm(true);
            setFormActionError('');
            await addJobForm(id, selectedFormId);
            setFeedback(TEXT.addFormSuccess);
            await loadJob();
            setIsAddFormOpen(false);
            setSelectedFormId('');
        } catch (err) {
            setFormActionError(err.response?.data?.message || TEXT.addFormError);
        } finally {
            setIsAddingForm(false);
        }
    };

    const handleAddEmptyRepeatableForm = async (templateId) => {
        if (!templateId || isJobReadOnly) {
            setFormActionError(TEXT.addFormClosed);
            return;
        }
        setAddingRepeatableTemplates((prev) => new Set(prev).add(String(templateId)));
        setFormActionError('');
        try {
            await addJobForm(id, templateId);
            setFeedback(TEXT.addFormSuccess);
            setDuplicateMenuKey('');
            await loadJob();
        } catch (err) {
            setFormActionError(err.response?.data?.message || TEXT.addFormError);
        } finally {
            setAddingRepeatableTemplates((prev) => {
                const next = new Set(prev);
                next.delete(String(templateId));
                return next;
            });
        }
    };

    const handleDuplicateRepeatableForm = async (form) => {
        if (!form?.jobFormId || isJobReadOnly) {
            setFormActionError(TEXT.addFormClosed);
            return;
        }
        setDuplicatingForms((prev) => new Set(prev).add(form.jobFormId));
        setFormActionError('');
        try {
            await duplicateJobForm(id, form.jobFormId);
            setFeedback(TEXT.addFormSuccess);
            setDuplicateMenuKey('');
            await loadJob();
        } catch (err) {
            setFormActionError(err.response?.data?.message || TEXT.addFormError);
        } finally {
            setDuplicatingForms((prev) => {
                const next = new Set(prev);
                next.delete(form.jobFormId);
                return next;
            });
        }
    };

    const handleRemoveManualForm = async (pdfFormId) => {
        if (!pdfFormId) return;
        if (isJobReadOnly) {
            setFormActionError(TEXT.removeFormClosed);
            return;
        }
        if (!window.confirm(TEXT.removeFormConfirm)) {
            return;
        }
        setRemovingForms((prev) => new Set(prev).add(pdfFormId));
        setFormActionError('');
        try {
            await removeJobForm(id, pdfFormId);
            setFeedback(TEXT.removeFormSuccess);
            await loadJob();
        } catch (err) {
            setFormActionError(err.response?.data?.message || TEXT.removeFormError);
        } finally {
            setRemovingForms((prev) => {
                const next = new Set(prev);
                next.delete(pdfFormId);
                return next;
            });
        }
    };

    const handleToggleArchiveJob = async () => {
        if (!job) return;
        const nextStatus = job.status === 'archived' ? 'open' : 'archived';
        const confirmText = nextStatus === 'archived' ? TEXT.archiveConfirm : TEXT.restoreConfirm;
        if (!window.confirm(confirmText)) {
            return;
        }
        setIsArchivingJob(true);
        setFeedback('');
        setFormActionError('');
        try {
            await updateJobStatus(job.id, nextStatus);
            setFeedback(nextStatus === 'archived' ? TEXT.jobArchiveSuccess : TEXT.jobRestoreSuccess);
            await loadJob();
        } catch (err) {
            setFormActionError(err.response?.data?.message || TEXT.jobArchiveError);
        } finally {
            setIsArchivingJob(false);
        }
    };

    const handleDownloadForm = async (form) => {
        if (!form?.jobFormId) return;
        setDownloadError('');
        setDownloadingForms((prev) => new Set(prev).add(form.jobFormId));
        try {
            const fallbackFilename = buildFormFilename(getFormDownloadName(form), job?.jobNumber);
            if (form.status === 'completed' && form.hasCachedPdf) {
                try {
                    const cached = await downloadCachedPdfFormExport(form.jobFormId);
                    saveBlob(cached.blob, fallbackFilename);
                    return;
                } catch (err) {
                    if (err.response?.status !== 404) {
                        throw err;
                    }
                }
            }
            const instance = await fetchPdfForm(form.jobFormId);
            const blob = await exportPdfFormWithOverlays({
                formId: instance.id,
                template: instance.template,
                fields: instance.template?.fields || [],
                values: instance.valuesJson || {},
            });
            const filename = buildFormFilename(
                getFormDownloadName({
                    ...form,
                    name: instance.template?.name || form.name,
                    template: instance.template,
                    instanceNumber: instance.instanceNumber ?? form.instanceNumber,
                }),
                job?.jobNumber,
            );
            if (form.status === 'completed') {
                await storeCachedPdfFormExport(form.jobFormId, {
                    pdfBlob: blob,
                    filename,
                });
            }
            saveBlob(blob, filename);
        } catch (err) {
            setDownloadError(err.response?.data?.message || TEXT.downloadError);
        } finally {
            setDownloadingForms((prev) => {
                const next = new Set(prev);
                next.delete(form.jobFormId);
                return next;
            });
        }
    };

    const openShareFormModal = (form) => {
        if (!form?.jobFormId) return;
        if (!form.isShareableByEmail) {
            setFormActionError(TEXT.shareFormNotEnabled);
            return;
        }
        if (form.status !== 'completed') {
            setFormActionError(TEXT.shareFormCompletedOnly);
            return;
        }
        if (form.isSharedByEmail) {
            setFormActionError(TEXT.shareFormAlreadySent);
            return;
        }
        setShareFormTarget(form);
        setShareFormEmail('');
        setShareFormSelectedRecipientIds(new Set());
        setShareFormExtraEmails([]);
        setShareFormError('');
    };

    const closeShareFormModal = () => {
        if (isSharingForm) return;
        setShareFormTarget(null);
        setShareFormEmail('');
        setShareFormSelectedRecipientIds(new Set());
        setShareFormExtraEmails([]);
        setShareFormError('');
    };

    const toggleSelectedRecipient = (recipientId) => {
        setSelectedRecipientIds((prev) => {
            const next = new Set(prev);
            const idValue = String(recipientId);
            if (next.has(idValue)) {
                next.delete(idValue);
            } else {
                next.add(idValue);
            }
            return next;
        });
    };

    const toggleShareFormRecipient = (recipientId) => {
        setShareFormSelectedRecipientIds((prev) => {
            const next = new Set(prev);
            const idValue = String(recipientId);
            if (next.has(idValue)) {
                next.delete(idValue);
            } else {
                next.add(idValue);
            }
            return next;
        });
        setShareFormError('');
    };

    const removeSelectedRecipientEmail = (email) => {
        const emailKey = String(email || '').trim().toLowerCase();
        setSelectedRecipientIds((prev) => {
            const next = new Set(prev);
            recipients.forEach((recipient) => {
                if (String(recipient.email || '').trim().toLowerCase() === emailKey) {
                    next.delete(String(recipient.id));
                }
            });
            return next;
        });
    };

    const removeShareFormRecipientEmail = (email) => {
        const emailKey = String(email || '').trim().toLowerCase();
        setShareFormSelectedRecipientIds((prev) => {
            const next = new Set(prev);
            recipients.forEach((recipient) => {
                if (String(recipient.email || '').trim().toLowerCase() === emailKey) {
                    next.delete(String(recipient.id));
                }
            });
            return next;
        });
        setShareFormExtraEmails((prev) =>
            prev.filter((item) => String(item || '').trim().toLowerCase() !== emailKey),
        );
    };

    const addShareFormEmail = () => {
        const trimmedEmail = shareFormEmail.trim();
        if (!isValidEmailAddress(trimmedEmail)) {
            setShareFormError(TEXT.shareFormInvalid);
            return;
        }
        if (shareFormRecipientEmails.some((email) => email.toLowerCase() === trimmedEmail.toLowerCase())) {
            setShareFormError(TEXT.shareFormDuplicate);
            return;
        }
        if (shareFormRecipientEmails.length >= EMAIL_RECIPIENT_LIMIT) {
            setShareFormError(TEXT.sendEmailTooManyRecipients);
            return;
        }
        setShareFormExtraEmails((prev) => [...prev, trimmedEmail]);
        setShareFormEmail('');
        setShareFormError('');
    };

    const handleShareFormEmailKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        addShareFormEmail();
    };

    const handleShareForm = async (event) => {
        event.preventDefault();
        if (!shareFormTarget?.jobFormId) return;
        const trimmedEmail = shareFormEmail.trim();
        if (trimmedEmail && !isValidEmailAddress(trimmedEmail)) {
            setShareFormError(TEXT.shareFormInvalid);
            return;
        }
        const recipientEmails = uniqueEmails([
            ...shareFormRecipientEmails,
            ...(trimmedEmail ? [trimmedEmail] : []),
        ]);
        if (recipientEmails.length === 0) {
            setShareFormError(TEXT.shareFormNoRecipients);
            return;
        }
        if (recipientEmails.length > EMAIL_RECIPIENT_LIMIT) {
            setShareFormError(TEXT.sendEmailTooManyRecipients);
            return;
        }
        setIsSharingForm(true);
        setShareFormError('');
        setFormActionError('');
        try {
            const data = await sharePdfFormByEmail(shareFormTarget.jobFormId, {
                toEmails: recipientEmails,
            });
            setJob((prev) => {
                if (!prev) return prev;
                const sharedIds = new Set(
                    Array.isArray(data?.sharedFormIds) && data.sharedFormIds.length > 0
                        ? data.sharedFormIds.map((formId) => String(formId))
                        : [String(shareFormTarget.jobFormId)],
                );
                const nextForms = (prev.pdfForms || []).map((form) =>
                    sharedIds.has(String(form.id))
                        ? {
                              ...form,
                              shareEmailSentAt: data?.shareEmailSentAt || new Date().toISOString(),
                              shareEmailRecipient: data?.shareEmailRecipient || recipientEmails.join(', '),
                          }
                        : form,
                );
                return { ...prev, pdfForms: nextForms };
            });
            setFeedback(TEXT.shareFormSuccess);
            setShareFormTarget(null);
            setShareFormEmail('');
            setShareFormSelectedRecipientIds(new Set());
            setShareFormExtraEmails([]);
        } catch (err) {
            setShareFormError(err.response?.data?.message || TEXT.shareFormError);
        } finally {
            setIsSharingForm(false);
        }
    };

    const getMergeForms = useCallback(() => {
        return enhancedForms
            .filter((form) => form.jobFormId && form.status === 'completed')
            .slice()
            .sort(compareFormsForJobOrder);
    }, [enhancedForms]);

    const handleDownloadMerged = async () => {
        if (!job) return;
        setDownloadError('');
        setIsDownloadingMerged(true);
        try {
            if (getMergeForms().length === 0) {
                throw new Error('no_forms');
            }
            const merged = await downloadJobMergedPdf(job.id);
            saveBlob(merged.blob, merged.filename || `${job.jobNumber || 'J-00000'}_forms.pdf`);
        } catch (err) {
            setDownloadError(
                err?.message === 'no_forms'
                    ? TEXT.sendEmailNoForms
                    : err.response?.data?.message || TEXT.downloadError,
            );
        } finally {
            setIsDownloadingMerged(false);
        }
    };

    const handleUploadGalleryPhoto = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !job?.id) return;
        if (isGalleryLocked) {
            event.target.value = '';
            return;
        }
        setGalleryError('');
        setIsGalleryUploading(true);
        try {
            const created = await uploadJobGalleryPhoto(job.id, file);
            setGalleryPhotos((prev) => [created, ...prev]);
        } catch (err) {
            setGalleryError(err.response?.data?.message || TEXT.galleryUploadError);
        } finally {
            setIsGalleryUploading(false);
            event.target.value = '';
        }
    };

    const handleDeleteGalleryPhoto = async (photoId) => {
        if (!job?.id || !photoId) return;
        if (isGalleryLocked) return;
        if (!window.confirm(TEXT.galleryDeleteConfirm)) return;
        setGalleryError('');
        setDeletingGalleryPhotos((prev) => new Set(prev).add(photoId));
        try {
            await deleteJobGalleryPhoto(job.id, photoId);
            setGalleryPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
            setActiveGalleryPhoto((prev) => (prev?.id === photoId ? null : prev));
        } catch (err) {
            setGalleryError(err.response?.data?.message || TEXT.galleryDeleteError);
        } finally {
            setDeletingGalleryPhotos((prev) => {
                const next = new Set(prev);
                next.delete(photoId);
                return next;
            });
        }
    };

    const handleDownloadGalleryZip = async () => {
        if (!job?.id || galleryPhotos.length === 0) return;
        setGalleryError('');
        setIsGalleryDownloading(true);
        try {
            const zip = await downloadJobGalleryZip(job.id);
            saveBlob(zip.blob, zip.filename || `${job.jobNumber || job.id}_images.zip`);
        } catch (err) {
            setGalleryError(err.response?.data?.message || TEXT.galleryDownloadError);
        } finally {
            setIsGalleryDownloading(false);
        }
    };

    const handleMarkFinished = () => {
        if (!job) return;
        setFinishError('');
        setDownloadError('');
        if (requiredIncomplete.length > 0) {
            setFinishError(TEXT.markFinishedMissing);
            return;
        }
        if (isEmailQueued) {
            window.alert(TEXT.emailQueuedLock);
            return;
        }
        if (!window.confirm(TEXT.markFinishedConfirm)) return;
        openSendEmailModal();
    };

    const handleReopenJob = async () => {
        if (!job) return;
        setFinishError('');
        if (isEmailQueued) {
            window.alert(TEXT.emailQueuedLock);
            return;
        }
        if (!window.confirm(TEXT.reopenConfirm)) return;
        try {
            setIsReopeningJob(true);
            await updateJobStatus(job.id, 'in_progress');
            setFeedback(TEXT.jobReopenSuccess);
            await loadJob();
        } catch (err) {
            setFinishError(err.response?.data?.message || TEXT.jobReopenError);
        } finally {
            setIsReopeningJob(false);
        }
    };

    const handleDeleteJob = async () => {
        if (!job?.id) return;
        if (!window.confirm(TEXT.deleteJobConfirm)) return;
        try {
            await deleteJob(job.id);
            navigate('/dashboard');
        } catch (err) {
            setFinishError(err.response?.data?.message || TEXT.deleteJobError);
        }
    };

    const formatCountdown = (seconds) => {
        if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = String(seconds % 60).padStart(2, '0');
        return `${mins}:${secs}`;
    };

    const buildEmailSubject = () => {
        const dateLabel = formatDate(new Date());
        const jobNumber = job?.jobNumber || 'J-00000';
        return `${jobNumber} טפסים - ${dateLabel}`;
    };

    const buildEmailBody = () => {
        const lines = [];
        if (job?.jobNumber) {
            lines.push(`מספר עבודה: ${job.jobNumber}`);
        }
        if (job?.recipientCompanyName) {
            lines.push(`לקוח: ${job.recipientCompanyName}`);
        }
        const sender =
            currentUser?.name ||
            currentUser?.email ||
            job?.assignedUser?.name ||
            job?.assignedUser?.email;
        if (sender) {
            lines.push(`נשלח על ידי: ${sender}`);
        }
        if (job?.notes && job.notes.trim()) {
            lines.push(`הערות עבודה: ${job.notes.trim()}`);
        }
        return lines.join('\n');
    };

    const openSendEmailModal = () => {
        if (selectedRecipientIds.size === 0 && recipients.length > 0) {
            const defaultRecipient =
                recipients.find((recipient) => recipient.isDefaultForFinishedJobs) || recipients[0];
            setSelectedRecipientIds(new Set([String(defaultRecipient.id)]));
        }
        setSendEmailError('');
        setIsSendEmailOpen(true);
    };

    const closeSendEmailModal = () => {
        if (isSendingEmail) return;
        setIsSendEmailOpen(false);
        setSendEmailError('');
    };

    const handleQueueEmail = async (event) => {
        event.preventDefault();
        setSendEmailError('');
        setFinishError('');
        if (selectedRecipientEmails.length === 0) {
            setSendEmailError(TEXT.sendEmailSelectRecipient);
            return;
        }
        if (selectedRecipientEmails.length > EMAIL_RECIPIENT_LIMIT) {
            setSendEmailError(TEXT.sendEmailTooManyRecipients);
            return;
        }
        setIsSendingEmail(true);
        try {
            if (getMergeForms().length === 0) {
                throw new Error('no_forms');
            }
            const data = await queueFinalizedJobEmail({
                jobId: job?.id,
                toEmails: selectedRecipientEmails,
                subject: buildEmailSubject(),
                bodyText: buildEmailBody(),
            });
            setEmailJob({
                id: data?.id,
                status: data?.status || 'queued',
                sendAt: data?.sendAt,
                sentAt: null,
                cancelAt: null,
                lastError: null,
            });
            if (emailJobStorageKey && data?.id && typeof window !== 'undefined') {
                window.localStorage.setItem(emailJobStorageKey, data.id);
            }
            setIsSendEmailOpen(false);
            await loadJob();
        } catch (err) {
            if (err?.message === 'no_forms') {
                setSendEmailError(TEXT.sendEmailNoForms);
            } else {
                setSendEmailError(err.response?.data?.message || TEXT.sendEmailFailed);
            }
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleCancelQueuedEmail = async () => {
        if (!emailJob?.id) return;
        setIsCancellingEmail(true);
        try {
            const data = await cancelQueuedEmail(emailJob.id);
            setEmailJob((prev) => ({
                ...(prev || {}),
                status: data?.status || 'cancelled',
                cancelAt: data?.cancelAt || new Date().toISOString(),
            }));
            clearStoredEmailJob();
            await loadJob();
        } catch (err) {
            setEmailJob((prev) => ({
                ...(prev || {}),
                status: 'failed',
                lastError: err.response?.data?.message || TEXT.sendEmailFailed,
            }));
        } finally {
            setIsCancellingEmail(false);
        }
    };

    useEffect(() => {
        if (!emailJob?.id || emailJob.status !== 'queued') return undefined;
        const interval = setInterval(async () => {
            try {
                const status = await fetchEmailStatus(emailJob.id);
                setEmailJob((prev) => ({
                    ...(prev || {}),
                    ...status,
                }));
            } catch {
                // ignore poll errors
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [emailJob?.id, emailJob?.status]);

    useEffect(() => {
        if (!emailJob?.sendAt || emailJob.status !== 'queued') {
            setEmailCountdown(0);
            return undefined;
        }
        const updateCountdown = () => {
            const remainingMs = new Date(emailJob.sendAt).getTime() - Date.now();
            const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
            setEmailCountdown(remainingSeconds);
        };
        updateCountdown();
        const timer = setInterval(updateCountdown, 1000);
        return () => clearInterval(timer);
    }, [emailJob?.sendAt, emailJob?.status]);

    useEffect(() => {
        if (!emailJob?.status || emailJob.status === 'queued') return undefined;
        clearStoredEmailJob();
        const timer = setTimeout(() => {
            setEmailJob(null);
        }, EMAIL_STATUS_BANNER_MS);
        return () => clearTimeout(timer);
    }, [clearStoredEmailJob, emailJob?.status]);

    const renderEmailBanner = () => {
        if (!emailJob) return null;
        if (emailJob.status === 'queued') {
            return (
                <div className="alert alert--info flex justify-between align-center">
                    <span>
                        {TEXT.sendEmailQueued} {formatCountdown(emailCountdown)}
                    </span>
                    <button
                        type="button"
                        className="button button--ghost"
                        onClick={handleCancelQueuedEmail}
                        disabled={isCancellingEmail}
                    >
                        {TEXT.sendEmailUndo}
                    </button>
                </div>
            );
        }
        if (emailJob.status === 'cancelled') {
            return <div className="alert alert--info">{TEXT.sendEmailCancelled}</div>;
        }
        if (emailJob.status === 'sent') {
            return <div className="alert alert--success">{TEXT.sendEmailSent}</div>;
        }
        if (emailJob.status === 'failed') {
            return (
                <div className="alert alert--danger">
                    {TEXT.sendEmailFailed}
                    {emailJob.lastError ? ` (${emailJob.lastError})` : ''}
                </div>
            );
        }
        return null;
    };

    const renderSendEmailModal = () => {
        if (!isSendEmailOpen) return null;
        return (
            <div
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        closeSendEmailModal();
                    }
                }}
            >
                <div className="modal-content" role="document">
                    <header className="stack">
                        <h2 className="card__title">{TEXT.sendEmailTitle}</h2>
                        <p className="card__meta">{TEXT.sendEmailDescription}</p>
                    </header>
                    <form className="modal-form" onSubmit={handleQueueEmail}>
                        <div className="stack modal-form__section">
                            <div className="form-field">
                                <span className="form-field__label">{TEXT.sendEmailTo}</span>
                                {recipients.length === 0 ? (
                                    <div className="modal-form__empty">
                                        {recipientsError || TEXT.sendEmailNoRecipients}
                                    </div>
                                ) : (
                                    <>
                                        <div className="recipient-picker" role="group" aria-label={TEXT.sendEmailTo}>
                                            {recipients.map((recipient) => {
                                                const recipientId = String(recipient.id);
                                                const isSelected = selectedRecipientIds.has(recipientId);
                                                return (
                                                    <label
                                                        key={recipient.id}
                                                        className={`recipient-option${isSelected ? ' recipient-option--selected' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleSelectedRecipient(recipientId)}
                                                            disabled={isSendingEmail}
                                                        />
                                                        <span className="recipient-option__body">
                                                            <span className="recipient-option__name">
                                                                {recipient.name}
                                                            </span>
                                                            <span className="recipient-option__email">
                                                                {recipient.email}
                                                            </span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        {selectedRecipientEmails.length > 0 && (
                                            <div className="selected-email-list" aria-label={TEXT.sendEmailSelected}>
                                                {selectedRecipientEmails.map((email) => (
                                                    <span key={email} className="selected-email-chip">
                                                        <span>{email}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeSelectedRecipientEmail(email)}
                                                            disabled={isSendingEmail}
                                                            aria-label={`${TEXT.sendEmailRemoveRecipient} ${email}`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="card__meta">
                                <strong>נושא:</strong> {buildEmailSubject()}
                            </div>
                        </div>

                        {sendEmailError && <div className="alert alert--danger">{sendEmailError}</div>}

                        <div className="modal-form__footer">
                            <button
                                type="button"
                                className="button button--ghost"
                                onClick={closeSendEmailModal}
                                disabled={isSendingEmail}
                            >
                                {TEXT.sendEmailCancel}
                            </button>
                            <button
                                type="submit"
                                className="button button--primary"
                                disabled={isSendingEmail}
                            >
                                {isSendingEmail ? TEXT.loading : TEXT.sendEmailSubmit}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const renderShareFormModal = () => {
        if (!shareFormTarget) return null;
        return (
            <div
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        closeShareFormModal();
                    }
                }}
            >
                <div className="modal-content" role="document">
                    <header className="stack">
                        <h2 className="card__title">{TEXT.shareFormTitle}</h2>
                        <p className="card__meta">{shareFormTarget.name}</p>
                        <p className="card__meta">{TEXT.shareFormDescription}</p>
                    </header>
                    <form className="modal-form" onSubmit={handleShareForm}>
                        <div className="modal-form__body">
                            {recipients.length > 0 && (
                                <div className="form-field">
                                    <span className="form-field__label">{TEXT.sendEmailTo}</span>
                                    <div className="recipient-picker" role="group" aria-label={TEXT.sendEmailTo}>
                                        {recipients.map((recipient) => {
                                            const recipientId = String(recipient.id);
                                            const isSelected = shareFormSelectedRecipientIds.has(recipientId);
                                            return (
                                                <label
                                                    key={recipient.id}
                                                    className={`recipient-option${isSelected ? ' recipient-option--selected' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleShareFormRecipient(recipientId)}
                                                        disabled={isSharingForm}
                                                    />
                                                    <span className="recipient-option__body">
                                                        <span className="recipient-option__name">{recipient.name}</span>
                                                        <span className="recipient-option__email">{recipient.email}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="form-field">
                                <span className="form-field__label">{TEXT.shareFormTo}</span>
                                <div className="recipient-add-row">
                                    <input
                                        className="form-field__control"
                                        type="email"
                                        value={shareFormEmail}
                                        onChange={(event) => setShareFormEmail(event.target.value)}
                                        onKeyDown={handleShareFormEmailKeyDown}
                                        disabled={isSharingForm}
                                        autoFocus={recipients.length === 0}
                                    />
                                    <button
                                        type="button"
                                        className="button button--ghost"
                                        onClick={addShareFormEmail}
                                        disabled={isSharingForm || !shareFormEmail.trim()}
                                    >
                                        {TEXT.shareFormAddRecipient}
                                    </button>
                                </div>
                            </div>
                            {shareFormRecipientEmails.length > 0 && (
                                <div className="selected-email-list" aria-label={TEXT.shareFormSelectedRecipients}>
                                    {shareFormRecipientEmails.map((email) => (
                                        <span key={email} className="selected-email-chip">
                                            <span>{email}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeShareFormRecipientEmail(email)}
                                                disabled={isSharingForm}
                                                aria-label={`${TEXT.sendEmailRemoveRecipient} ${email}`}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            {shareFormError && <div className="alert alert--danger">{shareFormError}</div>}
                        </div>
                        <footer className="modal-form__footer">
                            <button
                                type="button"
                                className="button button--ghost"
                                onClick={closeShareFormModal}
                                disabled={isSharingForm}
                            >
                                {TEXT.sendEmailCancel}
                            </button>
                            <button type="submit" className="button button--primary" disabled={isSharingForm}>
                                {isSharingForm ? TEXT.shareFormSending : TEXT.shareFormSubmit}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        );
    };

    const renderAddFormModal = () => {
        if (!isAddFormOpen) {
            return null;
        }
        const isEmptyList = availableForms.length === 0;
        const isEmptySearch = !isEmptyList && filteredAvailableForms.length === 0;

        return (
            <div
                className="modal-overlay"
                role="dialog"
                aria-modal="true"
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        closeAddFormModal();
                    }
                }}
            >
                <div className="modal-content" role="document">
                    <header className="stack">
                        <h2 className="card__title">{TEXT.addFormTitle}</h2>
                        <p className="card__meta">{TEXT.addFormDescription}</p>
                    </header>
                    <form className="modal-form" onSubmit={handleAddForm}>
                        <div className="stack modal-form__section">
                            <label className="form-field">
                                <span className="form-field__label">{TEXT.addFormSearch}</span>
                                <input
                                    className="form-field__control"
                                    type="search"
                                    placeholder={TEXT.addFormPlaceholder}
                                    value={formSearch}
                                    onChange={(event) => setFormSearch(event.target.value)}
                                    disabled={isEmptyList}
                                />
                            </label>
                            <div className="modal-form__list">
                                {isEmptyList ? (
                                    <div className="modal-form__empty">{TEXT.allFormsAttached}</div>
                                ) : isEmptySearch ? (
                                    <div className="modal-form__empty">{TEXT.noMatchingForms}</div>
                                ) : (
                                    filteredAvailableForms.map((form) => {
                                        const isSelected = selectedFormId === String(form.id);
                                        return (
                                            <label
                                                key={form.id}
                                                className={`selector-row ${isSelected ? 'selector-row--active' : ''}`}
                                            >
                                                <div className="flex align-start flex--wrap">
                                                    <input
                                                        type="radio"
                                                        name="job-form"
                                                        value={form.id}
                                                        checked={isSelected}
                                                        onChange={(event) => setSelectedFormId(event.target.value)}
                                                    />
                                                    <div className="stack">
                                                        <strong>{form.name}</strong>
                                                        {form.description && (
                                                            <span className="card__meta">{form.description}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {formActionError && <div className="alert alert--danger">{formActionError}</div>}

                        <div className="modal-form__footer">
                            <button
                                type="button"
                                className="button button--ghost"
                                onClick={closeAddFormModal}
                                disabled={isAddingForm}
                            >
                                {TEXT.addFormCancel}
                            </button>
                            <button
                                type="submit"
                                className="button button--primary"
                                disabled={isAddingForm || isEmptyList}
                            >
                                {isAddingForm ? TEXT.loading : TEXT.addFormSubmit}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="page page--job-details page--accent">

            {isLoading && <div className="card">{TEXT.loading}</div>}
            {error && <div className="alert alert--danger">{error}</div>}

            {!isLoading && !error && job && (
                <div className="stack stack--lg">
                    <header className="job-details-topbar">
                        <div className="job-details-topbar__main">
                            <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                                {TEXT.back}
                            </button>
                            <div className="stack stack--sm job-details-title-stack">
                                <div className="job-details-title-row">
                                    <h1 className="page__title">
                                        {TEXT.title}: {job.jobNumber}
                                    </h1>
                                    <span className={`status-badge ${STATUS_CLASS[job.status] || ''}`}>
                                        {STATUS_LABEL[job.status] || job.status}
                                    </span>
                                    <button
                                        type="button"
                                        className={`job-meta-toggle${isJobMetaVisible ? '' : ' is-hidden'}`}
                                        onClick={() => setIsJobMetaVisible((value) => !value)}
                                        aria-label={isJobMetaVisible ? TEXT.hideMetadata : TEXT.showMetadata}
                                        title={isJobMetaVisible ? TEXT.hideMetadata : TEXT.showMetadata}
                                    >
                                        i
                                    </button>
                                </div>
                                <div className="job-details-last-edited">
                                    <span className="job-meta__label">{TEXT.updatedAt}</span>
                                    <span className="job-meta__value">{formatDateTime(job.updatedAt) || '-'}</span>
                                </div>
                                {isJobMetaVisible && (
                                <div className="job-header-meta">
                                    <div className="job-header-meta__row">
                                        <div className="job-meta">
                                            <span className="job-meta__label">{TEXT.jobTypeLabel}:</span>
                                            <span className="job-meta__value">
                                                {jobTypeNames.length > 0 ? jobTypeNames.join(', ') : '-'}
                                            </span>
                                        </div>
                                        <div className="job-meta">
                                            <span className="job-meta__label">{TEXT.recipientLabel}:</span>
                                            <span className="job-meta__value">
                                                {job.recipientCompanyName || '-'}
                                            </span>
                                        </div>
                                        <div className="job-meta">
                                            <span className="job-meta__label">{TEXT.openedAtLabel}:</span>
                                            <span className="job-meta__value">
                                                {job.openedAt ? formatDate(job.openedAt) : '-'}
                                            </span>
                                        </div>
                                        <div className="job-meta">
                                            <span className="job-meta__label">{TEXT.openedByLabel}:</span>
                                            <span className="job-meta__value">
                                                {job.assignedUser?.name || job.assignedUser?.email || '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        </div>
                        <div className="job-details-topbar__actions">
                            {job.status === 'completed' ? (
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={handleReopenJob}
                                    disabled={isReopeningJob || isLoading}
                                >
                                    {isReopeningJob ? TEXT.reopeningJob : TEXT.reopenJob}
                                </button>
                            ) : job.status !== 'archived' ? (
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={handleMarkFinished}
                                    disabled={
                                        isSendingEmail ||
                                        isDownloadingMerged ||
                                        isLoading ||
                                        isEmailQueued
                                    }
                                >
                                    {isSendingEmail ? TEXT.loading : TEXT.downloadMerged}
                                </button>
                            ) : null}
                            {isAdminUser && (
                                <button
                                    type="button"
                                    className={job?.status === 'archived' ? 'button button--primary' : 'button button--danger'}
                                    onClick={handleToggleArchiveJob}
                                    disabled={isArchivingJob}
                                >
                                    {isArchivingJob
                                        ? job?.status === 'archived'
                                            ? TEXT.restoringJob
                                            : TEXT.archivingJob
                                        : job?.status === 'archived'
                                            ? TEXT.restoreJob
                                            : TEXT.archiveJob}
                                </button>
                            )}
                            {isAdminUser && (
                                <button
                                    type="button"
                                    className="button button--danger"
                                    onClick={handleDeleteJob}
                                >
                                    {TEXT.deleteJobPermanent}
                                </button>
                            )}
                        </div>
                    </header>

                    {feedback && <div className="alert alert--info">{feedback}</div>}
                    {renderEmailBanner()}
                    <div className="job-details-summary-grid">

                    <section className={`card job-panel${isNotesExpanded ? ' is-expanded' : ''}`}>
                        <button
                            type="button"
                            className="job-panel__toggle"
                            onClick={handleToggleNotesPanel}
                            aria-expanded={isNotesExpanded}
                        >
                            <span className="job-panel__headline">
                                <span className="job-panel__icon">
                                    <NotesIcon />
                                </span>
                                <span className="job-panel__copy">
                                    <span className="card__title">{TEXT.notesTitle}</span>
                                    <span className="card__meta">{notesSummary}</span>
                                </span>
                            </span>
                            <span className="job-panel__chevron">
                                <PanelChevronIcon expanded={isNotesExpanded} />
                            </span>
                        </button>
                        {isNotesExpanded && (
                        <div className="job-panel__content job-notes">
                        <div className="job-notes__header">
                            <div className="job-notes__actions">
                                {isEditingNotes ? (
                                    <>
                                        <button
                                            type="button"
                                            className="button button--ghost"
                                            onClick={cancelEditingNotes}
                                            disabled={notesSaving}
                                        >
                                            {TEXT.notesCancel}
                                        </button>
                                        <button
                                            type="button"
                                            className="button button--primary"
                                            onClick={handleSaveNotes}
                                            disabled={notesSaving}
                                        >
                                            {notesSaving ? TEXT.notesSaving : TEXT.notesSave}
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" className="button button--light" onClick={startEditingNotes}>
                                        {TEXT.notesEdit}
                                    </button>
                                )}
                            </div>
                        </div>
                        {isEditingNotes ? (
                            <textarea
                                className="form-field__control form-field__control--textarea"
                                rows={4}
                                value={notesDraft}
                                onChange={(event) => setNotesDraft(event.target.value)}
                                disabled={notesSaving}
                            />
                        ) : (
                            <p className={`card__meta${job.notes && job.notes.trim() ? '' : ' card__meta--muted'}`}>
                                {job.notes && job.notes.trim() ? job.notes : TEXT.notesEmpty}
                            </p>
                        )}
                        {notesError && <div className="alert alert--danger">{notesError}</div>}
                        {notesFeedback && <div className="alert alert--success">{notesFeedback}</div>}
                        </div>
                        )}
                    </section>

                    <section className={`card job-panel${isGalleryExpanded ? ' is-expanded' : ''}`}>
                        <div className="job-panel__header">
                            <button
                                type="button"
                                className="job-panel__toggle"
                                onClick={handleToggleGalleryPanel}
                                aria-expanded={isGalleryExpanded}
                            >
                                <span className="job-panel__headline">
                                    <span className="job-panel__icon">
                                        <GalleryIcon />
                                    </span>
                                    <span className="job-panel__copy">
                                        <span className="card__title">{TEXT.galleryTitle}</span>
                                        <span className="card__meta">{gallerySummary}</span>
                                    </span>
                                </span>
                                <span className="job-panel__chevron">
                                    <PanelChevronIcon expanded={isGalleryExpanded} />
                                </span>
                            </button>
                            <button
                                type="button"
                                className="icon-button job-panel__download"
                                onClick={handleDownloadGalleryZip}
                                disabled={isGalleryDownloading || isGalleryLoading || galleryPhotos.length === 0}
                                aria-label={TEXT.galleryDownloadAll}
                                title={TEXT.galleryDownloadAll}
                            >
                                <DownloadIcon size={21} />
                            </button>
                        </div>
                        {isGalleryExpanded && (
                        <div className="job-panel__content job-gallery">
                        <div className="job-notes__header">
                            <p className="card__meta">{TEXT.galleryDescription}</p>
                            <div className="job-notes__actions">
                                <label
                                    className={`button button--light job-gallery__upload-button${isGalleryLocked ? ' button--disabled' : ''}`}
                                    aria-label={TEXT.galleryUpload}
                                    title={TEXT.galleryUpload}
                                >
                                    <ImageUploadIcon />
                                    <span className="sr-only">{TEXT.galleryUpload}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleUploadGalleryPhoto}
                                        disabled={isGalleryUploading || isGalleryLocked}
                                    />
                                </label>
                                <label
                                    className={`button button--light job-gallery__upload-button${isGalleryLocked ? ' button--disabled' : ''}`}
                                    aria-label={TEXT.galleryCamera}
                                    title={TEXT.galleryCamera}
                                >
                                    <CameraIcon />
                                    <span className="sr-only">{TEXT.galleryCamera}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        hidden
                                        onChange={handleUploadGalleryPhoto}
                                        disabled={isGalleryUploading || isGalleryLocked}
                                    />
                                </label>
                            </div>
                        </div>
                        {galleryError && <div className="alert alert--danger">{galleryError}</div>}
                        {isGalleryLoading ? (
                            <div className="card__meta">{TEXT.galleryLoading}</div>
                        ) : galleryPhotos.length === 0 ? (
                            <div className="card__meta">{TEXT.galleryEmpty}</div>
                        ) : (
                            <div className="job-gallery__grid">
                                {galleryPhotos.map((photo) => (
                                    <article key={photo.id} className="job-gallery__item">
                                        <button
                                            type="button"
                                            className="job-gallery__thumb"
                                            onClick={() => setActiveGalleryPhoto(photo)}
                                        >
                                            <img
                                                src={resolveAssetUrl(photo.fileUrl)}
                                                alt={photo.originalName || 'תמונה'}
                                                className="job-gallery__image"
                                            />
                                        </button>
                                        <div className="job-gallery__meta">
                                            <span className="card__meta">
                                                {photo.originalName || 'תמונה'}
                                            </span>
                                            {photo.sizeBytes > 0 && (
                                                <span className="card__meta">
                                                    {formatFileSize(photo.sizeBytes)}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="button button--ghost"
                                                onClick={() => handleDeleteGalleryPhoto(photo.id)}
                                                disabled={
                                                    deletingGalleryPhotos.has(photo.id) || isGalleryLocked
                                                }
                                            >
                                                {TEXT.galleryDelete}
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                        </div>
                        )}
                    </section>

                    </div>
                    {finishError && <div className="alert alert--danger">{finishError}</div>}

                    <section className="card job-progress-card">
                        <div className="job-progress-inline">
                            <strong>{TEXT.progressTitle}</strong>
                            {formProgress.total === 0 ? (
                                <span className="card__meta">{TEXT.progressNone}</span>
                            ) : (
                                <>
                                    <div className="progress-bar" role="progressbar" aria-valuenow={formProgress.percent} aria-valuemin={0} aria-valuemax={100}>
                                        <div
                                            className="progress-bar__fill"
                                            style={{ width: `${formProgress.percent}%` }}
                                        />
                                    </div>
                                    <span className="card__meta">
                                        {formProgress.completed}/{formProgress.total} {TEXT.progressCaption}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="job-progress-card__actions">
                            <button
                                type="button"
                                className="button button--light"
                                onClick={handleDownloadMerged}
                                disabled={isDownloadingMerged || isSendingEmail || isLoading}
                            >
                                <DownloadIcon />
                                <span>{isDownloadingMerged ? TEXT.downloadingMerged : TEXT.downloadMergedOnly}</span>
                            </button>
                            <button
                                type="button"
                                className="button button--light"
                                onClick={openAddFormModal}
                                disabled={isAddingForm || isLoading || isJobReadOnly}
                                title={isJobReadOnly ? TEXT.addFormClosed : ''}
                            >
                                <PlusIcon />
                                <span>{TEXT.addForm}</span>
                            </button>
                            <button
                                type="button"
                                className={`thumbnail-toggle${showThumbnails ? ' is-active' : ''}`}
                                onClick={() => setShowThumbnails((value) => !value)}
                                aria-pressed={showThumbnails}
                                aria-label={TEXT.showThumbnails}
                                title={TEXT.showThumbnails}
                            >
                                <ThumbnailIcon />
                                <span className="thumbnail-toggle__pill" aria-hidden="true">
                                    <span className="thumbnail-toggle__knob" />
                                </span>
                            </button>
                        </div>
                    </section>

                    <section className="stack">
                        <div className="flex justify-between align-start flex--wrap">
                            <h2 className="card__title">{TEXT.forms}</h2>
                        </div>
                        {formActionError && !isAddFormOpen && (
                            <div className="alert alert--danger">{formActionError}</div>
                        )}
                        {downloadError && <div className="alert alert--danger">{downloadError}</div>}
                        {enhancedForms.length === 0 ? (
                            <div className="card">{TEXT.noFormsAttached}</div>
                        ) : (
                                <div className="card-grid card-grid--two job-form-card-list">
                                    {formGroups.map((group) => {
                                        if (group.isRepeatable) {
                                            const form = group.primaryForm;
                                            const templateId = form?.templateId || form?.template?.id;
                                            const addKey = String(templateId || group.id);
                                            const duplicateTarget = group.latestForm || form;
                                            const isAddingThis = addingRepeatableTemplates.has(addKey);
                                            const isDuplicatingThis = duplicateTarget?.jobFormId
                                                ? duplicatingForms.has(duplicateTarget.jobFormId)
                                                : false;
                                            const duplicateMenuId = `group-${group.id}`;
                                            const shareTarget = group.shareTarget;
                                            const shareTitle = !group.isShareableByEmail
                                                ? TEXT.shareFormNotEnabled
                                                : group.completedForms.length === 0
                                                    ? TEXT.shareFormCompletedOnly
                                                    : group.isSharedByEmail
                                                        ? TEXT.shareFormAlreadySent
                                                        : TEXT.shareForm;
                                            const instanceControlKey = `group-${group.id}`;
                                            const defaultIndex = Math.max(group.forms.length - 1, 0);
                                            const storedIndex = Number(repeatableFormIndices[instanceControlKey]);
                                            const activeFormIndex = Math.min(
                                                Math.max(Number.isInteger(storedIndex) ? storedIndex : defaultIndex, 0),
                                                defaultIndex,
                                            );
                                            const activeItem = group.forms[activeFormIndex] || group.forms[0];
                                            const canShowPreviousForm = activeFormIndex > 0;
                                            const canShowNextForm = activeFormIndex < group.forms.length - 1;
                                            const selectRepeatableFormIndex = (nextIndex) => {
                                                setRepeatableFormIndices((prev) => ({
                                                    ...prev,
                                                    [instanceControlKey]: Math.min(
                                                        Math.max(nextIndex, 0),
                                                        Math.max(group.forms.length - 1, 0),
                                                    ),
                                                }));
                                            };
                                            return (
                                                <article
                                                    key={`repeatable-${group.id}`}
                                                    className={[
                                                        'card',
                                                        'card--interactive',
                                                        'job-form-card',
                                                        'job-form-card--repeatable',
                                                        group.isRequiredActive
                                                            ? 'job-form-card--required'
                                                            : `job-form-card--${group.status}`,
                                                    ].filter(Boolean).join(' ')}
                                                >
                                                    <div className="job-form-card__duplicate">
                                                        <button
                                                            type="button"
                                                            className="icon-button job-form-card__duplicate-trigger"
                                                            onClick={() =>
                                                                setDuplicateMenuKey((value) =>
                                                                    value === duplicateMenuId ? '' : duplicateMenuId,
                                                                )
                                                            }
                                                            disabled={isJobReadOnly || !templateId}
                                                            aria-label={TEXT.duplicateForm}
                                                            title={TEXT.duplicateForm}
                                                        >
                                                            <DuplicateIcon />
                                                        </button>
                                                        {duplicateMenuKey === duplicateMenuId && (
                                                            <div className="job-form-card__duplicate-menu" role="menu" aria-label={TEXT.duplicateFormMenu}>
                                                                <button
                                                                    type="button"
                                                                    className="button button--light"
                                                                    onClick={() => handleDuplicateRepeatableForm(duplicateTarget)}
                                                                    disabled={isJobReadOnly || isDuplicatingThis || !duplicateTarget?.jobFormId}
                                                                >
                                                                    {isDuplicatingThis ? TEXT.duplicatingForm : TEXT.duplicateLatestForm}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="button button--light"
                                                                    onClick={() => handleAddEmptyRepeatableForm(templateId)}
                                                                    disabled={isJobReadOnly || isAddingThis || !templateId}
                                                                >
                                                                    {isAddingThis ? TEXT.loading : TEXT.addEmptyRepeatable}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {showThumbnails && form?.template?.id && (
                                                        <PdfThumbnail
                                                            fileUrl={`/api/pdf-templates/${form.template.id}/file`}
                                                            label={form.name}
                                                            width={96}
                                                            className="job-form-card__thumb"
                                                        />
                                                    )}
                                                    <header className="stack">
                                                        <div className="flex justify-between align-start">
                                                            <div className="stack">
                                                                <h3 className="card__title">{form?.name}</h3>
                                                                <div className="inline-pills">
                                                                    <span className="chip chip--info">{TEXT.repeatableForm}</span>
                                                                    <span className="chip">{group.forms.length} {TEXT.formInstances}</span>
                                                                    {group.isRequiredActive && (
                                                                        <span className="chip chip--danger">{TEXT.requiredLabel}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </header>

                                                    {activeItem && (
                                                        <div className="job-form-instance-card">
                                                            <div className="job-form-instance-card__header">
                                                                <button
                                                                    type="button"
                                                                    className="icon-button icon-button--compact job-form-instance-card__arrow"
                                                                    onClick={() => selectRepeatableFormIndex(activeFormIndex - 1)}
                                                                    disabled={!canShowPreviousForm}
                                                                    aria-label={TEXT.previousFormInstance}
                                                                    title={TEXT.previousFormInstance}
                                                                >
                                                                    <ArrowIcon direction="previous" />
                                                                </button>
                                                                <div className="job-form-instance-card__main">
                                                                    <strong>{TEXT.formInstance} #{activeItem.instanceNumber}</strong>
                                                                    <span className={`status-badge ${activeItem.statusClass}`}>{activeItem.statusLabel}</span>
                                                                    {activeItem.isEditing && (
                                                                        <span className="chip chip--warning">{activeItem.editingLabel}</span>
                                                                    )}
                                                                    <span className="card__meta">
                                                                        {TEXT.updatedAt}: {activeItem.updatedAt ? formatDate(activeItem.updatedAt) : '-'}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="icon-button icon-button--compact job-form-instance-card__arrow"
                                                                    onClick={() => selectRepeatableFormIndex(activeFormIndex + 1)}
                                                                    disabled={!canShowNextForm}
                                                                    aria-label={TEXT.nextFormInstance}
                                                                    title={TEXT.nextFormInstance}
                                                                >
                                                                    <ArrowIcon direction="next" />
                                                                </button>
                                                            </div>
                                                            <div className="job-form-instance-card__actions">
                                                                <button
                                                                    type="button"
                                                                    className="button button--primary"
                                                                    onClick={() => navigate(`/pdf-forms/${activeItem.jobFormId}`)}
                                                                >
                                                                    {TEXT.openForm}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="button button--light"
                                                                    onClick={() => handleDownloadForm(activeItem)}
                                                                    disabled={downloadingForms.has(activeItem.jobFormId)}
                                                                >
                                                                    {downloadingForms.has(activeItem.jobFormId)
                                                                        ? TEXT.downloadingForm
                                                                        : TEXT.downloadForm}
                                                                </button>
                                                                {activeItem.addedManually && (
                                                                    <button
                                                                        type="button"
                                                                        className="button button--danger"
                                                                        onClick={() => handleRemoveManualForm(activeItem.jobFormId)}
                                                                        disabled={removingForms.has(activeItem.jobFormId) || isJobReadOnly}
                                                                    >
                                                                        {removingForms.has(activeItem.jobFormId) ? TEXT.removingForm : TEXT.removeForm}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {group.isShareableByEmail && (
                                                        <footer className="flex flex--wrap job-form-card__bottom-actions">
                                                            <button
                                                                type="button"
                                                                className={[
                                                                    'button',
                                                                    'job-form-share-button',
                                                                    group.isSharedByEmail
                                                                        ? 'job-form-share-button--sent'
                                                                        : 'job-form-share-button--ready',
                                                                ].join(' ')}
                                                                onClick={() => openShareFormModal(shareTarget)}
                                                                disabled={
                                                                    isSharingForm ||
                                                                    !shareTarget?.jobFormId ||
                                                                    group.completedForms.length === 0 ||
                                                                    group.isSharedByEmail
                                                                }
                                                                title={shareTitle}
                                                            >
                                                                {group.isSharedByEmail ? <SharedCheckIcon /> : <SharePdfIcon />}
                                                                <span>{group.isSharedByEmail ? TEXT.shareFormSent : TEXT.shareAllForms}</span>
                                                            </button>
                                                        </footer>
                                                    )}
                                                    <div className="job-form-card__status">
                                                        <span className={`status-badge ${group.statusClass}`}>{group.statusLabel}</span>
                                                    </div>
                                                </article>
                                            );
                                        }

                                        const form = group.primaryForm;
                                        const templateId = form?.templateId || form?.template?.id;
                                        const addKey = String(templateId || form?.jobFormId || form?.id);
                                        const duplicateMenuId = `form-${form.jobFormId}`;
                                        const isAddingThis = addingRepeatableTemplates.has(addKey);
                                        const isDuplicatingThis = duplicatingForms.has(form.jobFormId);
                                        return (
                                            <article
                                                key={form.id}
                                                className={[
                                                    'card',
                                                    'card--interactive',
                                                    'job-form-card',
                                                    form.isRequiredActive
                                                        ? 'job-form-card--required'
                                                        : `job-form-card--${form.status}`,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            >
                                                <div className="job-form-card__duplicate">
                                                    <button
                                                        type="button"
                                                        className="icon-button job-form-card__duplicate-trigger"
                                                        onClick={() =>
                                                            setDuplicateMenuKey((value) =>
                                                                value === duplicateMenuId ? '' : duplicateMenuId,
                                                            )
                                                        }
                                                        disabled={isJobReadOnly || !templateId || !form.jobFormId}
                                                        aria-label={TEXT.duplicateForm}
                                                        title={TEXT.duplicateForm}
                                                    >
                                                        <DuplicateIcon />
                                                    </button>
                                                    {duplicateMenuKey === duplicateMenuId && (
                                                        <div className="job-form-card__duplicate-menu" role="menu" aria-label={TEXT.duplicateFormMenu}>
                                                            <button
                                                                type="button"
                                                                className="button button--light"
                                                                onClick={() => handleDuplicateRepeatableForm(form)}
                                                                disabled={isJobReadOnly || isDuplicatingThis || !form.jobFormId}
                                                            >
                                                                {isDuplicatingThis ? TEXT.duplicatingForm : TEXT.duplicateLatestForm}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="button button--light"
                                                                onClick={() => handleAddEmptyRepeatableForm(templateId)}
                                                                disabled={isJobReadOnly || isAddingThis || !templateId}
                                                            >
                                                                {isAddingThis ? TEXT.loading : TEXT.addEmptyRepeatable}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {showThumbnails && form.template?.id && (
                                                    <PdfThumbnail
                                                        fileUrl={`/api/pdf-templates/${form.template.id}/file`}
                                                        label={form.name}
                                                        width={96}
                                                        className="job-form-card__thumb"
                                                    />
                                                )}
                                                {form.isEditing && (
                                                    <span
                                                        className="job-form-card__editor"
                                                        title={form.editingLabel}
                                                        aria-label={form.editingLabel}
                                                    >
                                                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                            <circle cx="12" cy="8" r="4" fill="currentColor" />
                                                            <path
                                                                d="M4 20c1.6-4 5-6 8-6s6.4 2 8 6"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                )}
                                                <header className="stack">
                                                    <div className="flex justify-between align-start">
                                                        <div className="stack">
                                                            <h3 className="card__title">{form.name}</h3>
                                                            <div className="inline-pills">
                                                                {form.isRequiredActive && (
                                                                    <span className="chip chip--danger">{TEXT.requiredLabel}</span>
                                                                )}

                                                                {form.isEditing && (
                                                                    <span className="chip chip--warning">{form.editingLabel}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span className="card__meta">
                                                        {TEXT.updatedAt}: {form.updatedAt ? formatDate(form.updatedAt) : '-'}
                                                    </span>
                                                </header>
                                                {form.jobFormId && (
                                                    <footer className="flex flex--wrap">
                                                        <button
                                                            type="button"
                                                            className="button button--primary"
                                                            onClick={() => navigate(`/pdf-forms/${form.jobFormId}`)}
                                                        >
                                                            {TEXT.openForm}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button button--light"
                                                            onClick={() => handleDownloadForm(form)}
                                                            disabled={downloadingForms.has(form.jobFormId)}
                                                        >
                                                            {downloadingForms.has(form.jobFormId)
                                                                ? TEXT.downloadingForm
                                                                : TEXT.downloadForm}
                                                        </button>
                                                        {form.isShareableByEmail && (() => {
                                                            const shareTitle = !form.isShareableByEmail
                                                                ? TEXT.shareFormNotEnabled
                                                                : form.isSharedByEmail
                                                                    ? TEXT.shareFormAlreadySent
                                                                    : form.status !== 'completed'
                                                                        ? TEXT.shareFormCompletedOnly
                                                                        : TEXT.shareForm;
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className={[
                                                                        'button',
                                                                        'job-form-share-button',
                                                                        form.isSharedByEmail
                                                                            ? 'job-form-share-button--sent'
                                                                            : form.isShareableByEmail
                                                                                ? 'job-form-share-button--ready'
                                                                                : 'job-form-share-button--disabled',
                                                                    ].join(' ')}
                                                                    onClick={() => openShareFormModal(form)}
                                                                    disabled={
                                                                        isSharingForm ||
                                                                        !form.isShareableByEmail ||
                                                                        form.status !== 'completed' ||
                                                                        form.isSharedByEmail
                                                                    }
                                                                    title={shareTitle}
                                                                >
                                                                    {form.isSharedByEmail ? <SharedCheckIcon /> : <SharePdfIcon />}
                                                                    <span>
                                                                        {form.isSharedByEmail ? TEXT.shareFormSent : TEXT.shareForm}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })()}
                                                        {form.addedManually && (
                                                            <button
                                                                type="button"
                                                                className="button button--danger"
                                                                onClick={() => handleRemoveManualForm(form.jobFormId)}
                                                                disabled={removingForms.has(form.jobFormId) || isJobReadOnly}
                                                            >
                                                                {removingForms.has(form.jobFormId) ? TEXT.removingForm : TEXT.removeForm}
                                                            </button>
                                                        )}
                                                    </footer>
                                                )}
                                                <div className="job-form-card__status">
                                                    <span className={`status-badge ${form.statusClass}`}>{form.statusLabel}</span>
                                                </div>
                                            </article>
                                        );
                                    })}
                            </div>
                        )}
                    </section>
                </div>
            )}

            {activeGalleryPhoto && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            setActiveGalleryPhoto(null);
                        }
                    }}
                >
                    <div className="modal-content job-gallery__viewer" role="document">
                        <header className="job-gallery__viewer-header">
                            <h2 className="card__title">{activeGalleryPhoto.originalName || 'תמונה'}</h2>
                            <button
                                type="button"
                                className="button button--ghost"
                                onClick={() => setActiveGalleryPhoto(null)}
                            >
                                סגור
                            </button>
                        </header>
                        <img
                            src={resolveAssetUrl(activeGalleryPhoto.fileUrl)}
                            alt={activeGalleryPhoto.originalName || 'תמונה'}
                            className="job-gallery__viewer-image"
                        />
                    </div>
                </div>
            )}

            {renderAddFormModal()}
            {renderSendEmailModal()}
            {renderShareFormModal()}
        </div>
    );
}
